import {Injectable, OnDestroy} from '@angular/core';
import {liquidityPoolOrderEntryFacetAbi} from 'dex-abi/contracts/LiquidityPoolOrderEntryFacet';
import {
    BehaviorSubject,
    defer,
    merge,
    NEVER,
    Observable,
    of,
    shareReplay,
    Subject,
    Subscription,
    switchMap,
    timer
} from 'rxjs';
import {fromPromise} from 'rxjs/internal/observable/innerFrom';
import {distinctUntilChanged, map, repeat, takeUntil, tap} from 'rxjs/operators';
import {
    erc20Abi,
    Hash,
    isAddress,
    Log,
    parseEventLogs,
    Transaction,
    TransactionReceipt,
    TransactionReceiptNotFoundError
} from 'viem';
import {destroy$} from '../../components/destroyable';
import {CommonBlockchainUtilService} from '../../components/dex/common-blockchain-util.service';
import {DexMdSubjectService} from '../../components/dex/dex-streaming/subjects/dex-md-subject-service';
import {DexSecuritiesSubjectService} from '../../components/dex/dex-streaming/subjects/dex-securities-subject-service';
import {DeliverableId} from '../../components/dex/dex-streaming/types/deliverable';
import {DexOnChainOrderSide} from '../../components/dex/dex-streaming/types/dex-order';
import {vToDec} from '../../components/dex/dex-streaming/utils';
import {Erc20BalanceUpdatesService} from '../../components/dex/dyn-balances/erc20-balances-updates.service';
import {DynWallet} from '../../components/dynamic-xyz/dyn-xyz-wc/dyn-remote';
import {DynamicZyxDaemonService} from '../../components/dynamic-xyz/dynamic-zyx-daemon.service';
import {Side} from '../../enums/side';
import {UserService} from '../../services/user.service';
import {
    TxHistoryDepositLogsMeta,
    TxHistoryFailureReason,
    TxHistoryLog,
    TxHistoryOrderEnteredEvent,
    TxHistoryOrderLogsMeta,
    TxHistoryRec,
    TxHistoryRecMethod,
    TxHistoryStatus,
    TxHistoryTransferMeta,
    TxHistoryWithdrawLogsMeta,
    TxHistoryWithdrawMeta
} from './tx-history-rec';
import {TxMonitoringStorageService} from './tx-storage/tx-monitoring-storage.service';

@Injectable()
export class TxStatusMonitoringService implements OnDestroy {

    // Events for subscription from other components
    public readonly txStatusUpdated$ = new Subject<TxHistoryRec>();
    public readonly txConfirmed$ = new Subject<TxHistoryRec>();
    public readonly txFailed$ = new Subject<TxHistoryRec>();
    public readonly txDone$ = new Subject<TxHistoryRec>();

    private readonly CONFIRMATION_BLOCKS = 1; // Number of blocks required for final confirmation
    private readonly POLLING_START_DELAY = 1000; // 1 seconds
    private readonly POLLING_INTERVAL = 5000; // 5 seconds for PENDING, increases for CONFIRMED
    private readonly MAX_POLLING_BACKOFF = 60000; // Maximum polling interval // TODO
    private readonly TX_TIMEOUT_MS = 24 * 60 * 60 * 1000; // 24 hours timeout

    private monitoringSubscription?: Subscription;
    private transactionAddedSubscription?: Subscription;
    private readonly destroy$ = destroy$();
    private isMonitoring = false;

    private readonly publicClient = this.commonBlockchainUtilService.getPublicClient();

    private readonly checkingTxHashes = new Set<string>();

    /**
     * The flag tells, that existing failed transactions were already retried.
     */
    private isInitiallyFailedRetried: boolean = false;

    constructor(
        private txMonitoringStorageService: TxMonitoringStorageService,
        private dynamicZyxDaemonService: DynamicZyxDaemonService,
        private commonBlockchainUtilService: CommonBlockchainUtilService,
        private erc20BalanceUpdatesService: Erc20BalanceUpdatesService,
        private userService: UserService,
        private dexMdSubjectService: DexMdSubjectService,
        private dexSecuritiesSubjectService: DexSecuritiesSubjectService
    ) {
        this.initBalancesUpdates();
    }

    public ngOnDestroy(): void {
        this.stopMonitoring();
    }

    /**
     * Start monitoring - the combination of polling and listening
     */
    public startMonitoring(): void {
        if (this.isMonitoring) return;

        const isInitiallyProcessed$ = new BehaviorSubject<boolean>(false);

        // 1. Polling for reliability (always works)
        const processPollingTick = async () => {
            if (this.isMonitoring && this.userService.user) {
                try {
                    await this.checkPendingTransactions(!this.isInitiallyFailedRetried);
                    await this.checkConfirmedTransactions();
                } catch (error) {
                    console.error('[TxStatusMonitoring] Error in processPollingTick:', error);
                }

                this.isInitiallyFailedRetried = true;

                if (!isInitiallyProcessed$.value) {
                    isInitiallyProcessed$.next(true);
                }
            }
        };

        const polling$ = defer(processPollingTick).pipe(
            repeat({delay: this.POLLING_INTERVAL})
        );

        /**
         * Delay the first check.
         * The subscription may be called because of just created tx.
         * That tx may not be yet in the blockchain, so this delay
         * may prevent the initial receipt fetching failure.
         */
        const delayedPolling$ = timer(this.POLLING_START_DELAY).pipe(
            switchMap(() => polling$)
        );

        // 2. Listening for reactivity (if the provider supports)
        const newBlock$ = isInitiallyProcessed$.pipe(
            switchMap(state => state
                ? this.listenToBlockNumber()
                : NEVER
            )
        );

        const allProcessing$ = defer(() => {
            isInitiallyProcessed$.next(false);

            return merge(delayedPolling$, newBlock$);
        });

        // TxMonitoringStorageService.init needs user
        this.monitoringSubscription = this.userService.user$.pipe(
            switchMap(() => fromPromise(this.txMonitoringStorageService.init()))
        ).pipe(
            switchMap(() => this.createHasTxToMonitorStream()),
            switchMap(state => state
                ? allProcessing$
                : NEVER
            ),
            takeUntil(this.destroy$)
        ).subscribe();

        this.isMonitoring = true;
        console.log('[TxStatusMonitoring] Monitoring started');
    }

    public stopMonitoring(): void {
        this.isMonitoring = false;
        this.monitoringSubscription?.unsubscribe();
        this.transactionAddedSubscription?.unsubscribe();
        console.log('[TxStatusMonitoring] Monitoring stopped');

        // TODO: Do stop listeners in txMonitoringStorage?
    }

    private failedTxToRetryFilterFn(th: TxHistoryRec): boolean {
        return !th.failureReason;
    }

    private createHasTxToMonitorStream(): Observable<boolean> {
        const initialState$ = defer(() => {
            const includeFailed = !this.isInitiallyFailedRetried;
            const state = this.txMonitoringStorageService.hasTransactionsToMonitor(includeFailed);

            return of(state);
        });

        const updates$: Observable<boolean> = this.txMonitoringStorageService.anyTxUpdate$.pipe(
            map(th =>
                this.isTransactionActive(th)
                    ? true
                    : this.txMonitoringStorageService.hasTransactionsToMonitor()
            )
        );

        return merge(
            initialState$,
            updates$
        ).pipe(
            shareReplay({bufferSize: 1, refCount: true}),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        );
    }

    /**
     * Setup block number listening using Viem
     */
    private listenToBlockNumber(): Observable<void> {
        if (!this.publicClient.transport.type.includes('webSocket')) {
            console.log('[TxStatusMonitoring] WebSocket transport not available, using polling only');
            return NEVER;
        }

        return new Observable<void>(observer =>
            this.publicClient.watchBlockNumber({
                onBlockNumber: async (blockNumber) => {
                    if (this.isMonitoring) {
                        try {
                            console.log(`[TxStatusMonitoring] New block detected: ${blockNumber}`);
                            await this.processNewBlock(blockNumber);
                            observer.next();
                        } catch (error) {
                            console.error('[TxStatusMonitoring] Error processing new block:', error);
                        }
                    }
                },
                onError:       (error) => {
                    console.error('[TxStatusMonitoring] Block number watch error:', error);
                }
            })
        );
    }

    /**
     * Check only PENDING transactions (fast polling)
     */
    private async checkPendingTransactions(includeFailedRetries: boolean = false): Promise<void> {
        try {
            const ths = this.txMonitoringStorageService.getPendingTransactions(includeFailedRetries);

            if (ths.length === 0) return;

            console.log('[TxStatusMonitoring] checkPendingTransactions', ths); // TODO

            await Promise.allSettled(
                ths.map(th => this.skipDoubleTxProcessing(th, () => this.processPendingTransaction(th)))
            );
        } catch (error) {
            console.error('[TxStatusMonitoring] Error in checkPendingTransactions:', error);
        }
    }

    /**
     * Check CONFIRMED transactions
     */
    private async checkConfirmedTransactions(): Promise<void> {
        try {
            const ths = this.txMonitoringStorageService.getConfirmedTransactions();

            if (ths.length === 0) return;

            console.log('[TxStatusMonitoring] checkConfirmedTransactions', ths); // TODO

            await Promise.allSettled(
                ths.map(th => this.skipDoubleTxProcessing(th, () => this.processConfirmedTransaction(th)))
            );
        } catch (error) {
            console.error('[TxStatusMonitoring] Error in checkConfirmedTransactions:', error);
        }
    }

    private async skipDoubleTxProcessing<T>(
        th: TxHistoryRec,
        processFn: (th: TxHistoryRec) => Promise<T>
    ): Promise<T | undefined> {
        let result: T | undefined;

        if (!this.checkingTxHashes.has(th.hash)) {
            this.checkingTxHashes.add(th.hash);
            result = await processFn(th);
            this.checkingTxHashes.delete(th.hash);
        } else {
            console.log('[TxStatusMonitoring] Skipping tx, that\'s already precessing.');
        }

        return result;
    }

    private async processConfirmedTransaction(th: TxHistoryRec): Promise<void> {
        console.log('[TxStatusMonitoring] processConfirmedTransaction', th); // TODO

        const currentBlock = await this.publicClient.getBlock({blockTag: 'latest'});
        const txBlockNumber = th.blockNumber && BigInt(th.blockNumber) || 0n;
        const isEnough = currentBlock.number - txBlockNumber >= this.CONFIRMATION_BLOCKS;

        console.log('[TxStatusMonitoring] processConfirmedTransaction', {
            isEnough,
            n: currentBlock.number - txBlockNumber,
            currentBlock,
            txBlockNumber
        }); // TODO

        if (isEnough) {
            await this.finalizeTransaction(th.hash, {
                number: currentBlock.number,
                timestamp: Number(currentBlock.timestamp)}
            );
        }
    }

    /**
     * Process new block - check CONFIRMED transactions
     */
    private async processNewBlock(blockNumber: bigint): Promise<void> {
        await this.processNewBlockForPending();
        await this.processNewBlockForConfirmed(blockNumber);
    }

    /**
     * Process new block - check CONFIRMED transactions
     */
    private async processNewBlockForConfirmed(blockNumber: bigint): Promise<void> {
        try {
            // Check transactions that have reached the required number of confirmations
            const ths = this.txMonitoringStorageService.getConfirmedTransactions()
                .filter(th =>
                    th.blockNumber
                    && blockNumber - BigInt(th.blockNumber) >= this.CONFIRMATION_BLOCKS
                    && !this.checkingTxHashes.has(th.hash)
                );

            if (ths.length > 0) {
                const currentBlock = await this.publicClient.getBlock({blockNumber});

                await Promise.allSettled(
                    ths.map(th => this.skipDoubleTxProcessing(th, () => this.finalizeTransaction(th.hash, {
                        number:    currentBlock.number,
                        timestamp: Number(currentBlock.timestamp)
                    })))
                );
            }
        } catch (error) {
            console.error('[TxStatusMonitoring] Error in processNewBlockForConfirmed:', error);
        }
    }

    /**
     * Process new block - check PENDING transactions
     */
    private async processNewBlockForPending(): Promise<void> {
        try {
            await this.checkPendingTransactions();
        } catch (error) {
            console.error('[TxStatusMonitoring] Error in processNewBlockForPending:', error);
        }
    }

    /**
     * Process the single transaction
     */
    private async processPendingTransaction(th: TxHistoryRec): Promise<void> {
        console.log('[TxStatusMonitoring] processPendingTransaction', th); // TODO

        try {
            const currentBlock = await this.publicClient.getBlock({blockTag: 'latest'});
            const [receipt, tx] = await Promise.all([
                this.publicClient.getTransactionReceipt({hash: th.hash as Hash}),
                this.publicClient.getTransaction({hash: th.hash as Hash})
            ]);

            // Transaction is not in the block yet - updating updatedAt with the current block
            if (!receipt) {
                const updatedAt = Number(currentBlock.timestamp) * 1000;

                if (updatedAt > th.updatedAt) {
                    th = await this.txMonitoringStorageService.updateTx(th.hash, {
                        updatedAt: Number(currentBlock.timestamp) * 1000
                    });
                    await this.checkTransactionTimeout(th.hash, Number(currentBlock.timestamp));
                }

                return;
            }

            // Get transaction block for accurate timestamp
            const txBlock = await this.publicClient.getBlock({blockNumber: receipt.blockNumber});

            const blockInfo = {
                blockNumber: receipt.blockNumber,
                blockTimestamp: Number(txBlock.timestamp)
            };

            await this.updateDataFromReceiptAndTx(th, receipt, tx);

            // Update status based on receipt
            if (receipt.status === 'reverted') {
                await this.failTransaction(th.hash, blockInfo, TxHistoryFailureReason.REVERTED);
            } else if (receipt.status === 'success') {
                await this.confirmTransaction(th.hash, blockInfo);
            }
        } catch (error: unknown) {
            console.error(`[TxStatusMonitoring] Error processing tx ${th.hash}:`, error);

            // If transaction receipt not found (transaction is too new), treat like pending
            // This can happen when we check for receipt too early after transaction is placed
            if (error instanceof TransactionReceiptNotFoundError) {
                const currentBlock = await this.publicClient.getBlock({blockTag: 'latest'});

                const updatedAt = Number(currentBlock.timestamp) * 1000;

                if (updatedAt > th.updatedAt) {
                    th = await this.txMonitoringStorageService.updateTx(th.hash, {
                        blockNumber: String(currentBlock.number),
                        updatedAt
                    });
                    await this.checkTransactionTimeout(th.hash, Number(currentBlock.timestamp));
                }
            } else {
                // If network error (e.g. too many requests), retry later
                if (this.isNetworkError(error)) {
                    // Exponential backoff timer for next check
                    await this.scheduleRetry(th);
                } else {
                    await this.failTransaction(th.hash, undefined, TxHistoryFailureReason.UNKNOWN);
                }
            }
        }
    }

    /**
     * Confirm transaction
     */
    private async confirmTransaction(
        txHash: string,
        blockInfo: { blockNumber: bigint; blockTimestamp: number }
    ): Promise<void> {
        const updatedTx = await this.txMonitoringStorageService.updateTx(txHash, {
            status:      TxHistoryStatus.CONFIRMED,
            blockNumber: String(blockInfo.blockNumber),
            updatedAt:   blockInfo.blockTimestamp * 1000
        });

        if (updatedTx) {
            this.txStatusUpdated$.next(updatedTx);
            this.txConfirmed$.next(updatedTx);
            console.log(`[TxStatusMonitoring] Transaction confirmed: ${txHash} at block ${blockInfo.blockNumber}`);
        }
    }

    /**
     * Finalize transaction (enough confirmations)
     */
    private async finalizeTransaction(
        txHash: string,
        currentBlock: { number: bigint; timestamp: number }
    ): Promise<void> {
        const th = this.txMonitoringStorageService.getTransactionByHash(txHash);

        if (!th || th.status !== TxHistoryStatus.CONFIRMED) return;

        console.log('[TxStatusMonitoring] Finalize transaction:', txHash);

        const confirmations = currentBlock.number - (th.blockNumber ? BigInt(th.blockNumber) : 0n);

        if (confirmations >= this.CONFIRMATION_BLOCKS) {
            const updatedTx = await this.txMonitoringStorageService.updateTx(th.hash, {
                status:      TxHistoryStatus.DONE,
                blockNumber: String(currentBlock.number),
                updatedAt:   currentBlock.timestamp * 1000
            });

            if (updatedTx) {
                this.txStatusUpdated$.next(updatedTx);
                this.txDone$.next(updatedTx);
                console.log(`[TxStatusMonitoring] Transaction finalized: ${th.hash}`);
            }
        }
    }

    /**
     * Mar the transaction as FAILED
     */
    private async failTransaction(
        txHash: string,
        blockInfo?: { blockNumber?: bigint | string; blockTimestamp: number },
        failureReason?: TxHistoryFailureReason
    ): Promise<void> {
        const updatedTx = await this.txMonitoringStorageService.updateTx(txHash, {
            status:      TxHistoryStatus.FAILED,
            failureReason,
            blockNumber: String(blockInfo?.blockNumber),
            updatedAt:   blockInfo?.blockTimestamp * 1000
        });

        if (updatedTx) {
            this.txStatusUpdated$.next(updatedTx);
            this.txFailed$.next(updatedTx);
            console.warn(`[TxStatusMonitoring] Transaction failed: ${txHash}`);
        }
    }

    /**
     * Check timeout relative to block time
     */
    private async checkTransactionTimeout(
        txHash: string,
        currentBlockTimestamp: number
    ): Promise<void> {
        const th = this.txMonitoringStorageService.getTransactionByHash(txHash);

        if (!th) return;

        // Time difference in seconds between current block and last update block
        const txAgeMs = currentBlockTimestamp * 1000 - th.updatedAt;

        if (txAgeMs * 1000 > this.TX_TIMEOUT_MS) {
            console.warn(`[TxMonitor] Transaction ${th.hash} timed out after ${Math.floor(txAgeMs / 1000)}s`);

            await this.failTransaction(th.hash, {
                blockTimestamp: currentBlockTimestamp
            }, TxHistoryFailureReason.TIMEOUT);
        }
    }

    /**
     * Exponential backoff for retries
     */
    private async scheduleRetry(th: TxHistoryRec): Promise<void> {
        // TODO
        console.warn(`[TxMonitor] scheduleRetry ${th.hash}`);
    }

    /**
     * Check if transaction can change status
     */
    private isTransactionActive(th: TxHistoryRec): boolean {
        return !(
            th.status === TxHistoryStatus.DONE ||
            th.status === TxHistoryStatus.FAILED
        );
    }

    private isNetworkError(error: any): boolean {
        return error?.code === 'NETWORK_ERROR' ||
            error?.code === 'SERVER_ERROR' ||
            error?.code === 'TIMEOUT';
    }

    private async getReceipt(thRec: TxHistoryRec): Promise<void> {
        const receipt = await this.publicClient.waitForTransactionReceipt({
            hash: thRec.hash as Hash,
            confirmations: 1
        });

        console.log('[TxStatusMonitoring] Receipt:', receipt);
    }

    private getWalletByThRec(thRec: TxHistoryRec): DynWallet {
        const dynRemote = this.dynamicZyxDaemonService.dynRemote;

        const walletAddress = (thRec.metadata?.method === TxHistoryRecMethod.DEPOSIT)
            ? thRec.from
            : (thRec.metadata?.method === TxHistoryRecMethod.WITHDRAW)
                ? thRec.from
                : undefined;

        if (!walletAddress || !isAddress(walletAddress)) {
            throw new Error(`Invalid wallet address from Transaction History Record (txHash: ${thRec.hash})`);
        }

        const wallet = dynRemote.userWallets$.value.find(w => w.address === walletAddress);

        if (!wallet) {
            throw new Error(`Wallet not found for address ${walletAddress} (txHash: ${thRec.hash})`);
        }

        return wallet;
    }

    private async updateDataFromReceiptAndTx(
        th: TxHistoryRec,
        receipt: TransactionReceipt,
        tx: Transaction
    ): Promise<void> {
        const thUpdate: Partial<{
            gasUsedNative: string;
            logs: TxHistoryRec['logs'],
            logsMeta: TxHistoryRec['logsMeta']
        }> = {};

        await this.processGas(receipt, tx, thUpdate);

        switch (th.metadata.method) {
            case TxHistoryRecMethod.DEPOSIT:
            case TxHistoryRecMethod.WITHDRAW: {
                this.processOrderReceiptLogs(receipt, thUpdate);
                break;
            }
            case TxHistoryRecMethod.TRANSFER: {
                this.processTransferReceiptLogs(receipt, thUpdate);
                break;
            }
        }

        await this.txMonitoringStorageService.updateTx(th.hash, thUpdate);
    }

    private async processGas(
        receipt: TransactionReceipt,
        tx: Transaction,
        thUpdate: { gasUsedNative?: string; }
    ): Promise<void> {
        let gasPrice: bigint;

        if (tx.type === 'legacy') {
            gasPrice = tx.gasPrice;
        } else if (tx.type === 'eip1559') {
            gasPrice = receipt.effectiveGasPrice ||
                (tx.maxFeePerGas - tx.maxPriorityFeePerGas);
        }

        const gasUsed = receipt.gasUsed;

        if (gasUsed && gasPrice) {
            const totalCost = gasUsed * gasPrice;
            thUpdate.gasUsedNative = String(totalCost);
        }
    }

    private processOrderReceiptLogs(
        receipt: TransactionReceipt,
        thUpdate: {
            logs?: TxHistoryRec['logs'],
            logsMeta?: TxHistoryRec['logsMeta']
        }
    ): void {
        const orderEnteredEvents = parseEventLogs({
            abi:       liquidityPoolOrderEntryFacetAbi,
            logs:      receipt.logs,
            eventName: 'OrderEnteredEvent'
        });
        const transferEvents = parseEventLogs({
            abi:       erc20Abi, // ierc1363Abi,
            logs:      receipt.logs,
            eventName: 'Transfer'
        });

        const mergedLogs = ([
            ['LiquidityPoolOrderEntry', orderEnteredEvents],
            ['ERC20', transferEvents]
        ] satisfies Array<[string, Log[]]>).reduce(
            (acc, [contractName, events]) => {
                events.forEach(e => {
                    acc.push(this.createTxHistoryLog(contractName, e));
                });

                return acc;
            },
            [] satisfies TxHistoryLog[]
        );

        const histOrderEnteredEvent: TxHistoryOrderEnteredEvent = orderEnteredEvents.map(e => {
            const args = e.args;
            const orderEntered = args.orderEntered;
            const newOrder = orderEntered.newOrder;

            const bigintToStr = (v: bigint | undefined | null): string =>
                v !== undefined && v !== null ? String(v) : undefined;

            const bigintToNumber = (v: bigint | undefined | null): number =>
                v !== undefined && v !== null ? Number(v) : undefined;

            const dId: DeliverableId = bigintToNumber(newOrder.partyData.orderCollateral.deliverableId);
            const d = this.dexSecuritiesSubjectService.getDeliverableById(dId);

            return {
                address:           args.owner,
                orderId:           bigintToStr(args.orderId),
                tradeableEntityId: bigintToNumber(newOrder.tradeableEntityId),
                side:              newOrder.side === DexOnChainOrderSide.BUY ? Side.BUY : Side.SELL,
                deliverableId:     dId,
                quantityDec:       vToDec(newOrder.partyData.orderCollateral.quantity, d.decimalPlaces).toString()
            } satisfies TxHistoryOrderEnteredEvent;
        })[0];

        const logsMeta: TxHistoryOrderLogsMeta = {
            orderEnteredEvent: histOrderEnteredEvent
        };

        thUpdate.logs = mergedLogs;
        thUpdate.logsMeta = logsMeta;
    }

    private processTransferReceiptLogs(
        receipt: TransactionReceipt,
        thUpdate: {
            logs?: TxHistoryRec['logs'],
            logsMeta?: TxHistoryRec['logsMeta']
        }
    ): void {
        const transferEvents = parseEventLogs({
            abi:       erc20Abi,
            logs:      receipt.logs,
            eventName: 'Transfer'
        });

        const mergedLogs = ([
            ['ERC20', transferEvents]
        ] satisfies Array<[string, Log[]]>).reduce(
            (acc, [contractName, events]) => {
                events.forEach(e => {
                    acc.push(this.createTxHistoryLog(contractName, e));
                });

                return acc;
            },
            [] satisfies TxHistoryLog[]
        );

        console.warn('[T]', {
            receipt,
            transferEvents,
            mergedLogs
        }); // TODO

        // const histTransferEvents: TxHistoryTransferEvent[] = transferEvents.map(e => {
        //     const args = e.args;
        //     return {
        //         deliverableId:   Number(args.tokenId || 0), // Default to 0 if not available
        //         amount:    String(args.value),
        //         from:      args.from,
        //         to:        args.to,
        //         timestamp: String(receipt.blockTimestamp)
        //     } satisfies TxHistoryTransferEvent;
        // });

        // const logsMeta: TxHistoryTransferLogsMeta = {
        //     transferEvents: histTransferEvents
        // };

        thUpdate.logs = mergedLogs;
        // thUpdate.logsMeta = logsMeta; // TODO
    }

    private createTxHistoryLog(contractName: string, e: Log): TxHistoryLog {
        return {
            contract: contractName,
            event:    (e as any).eventName,
            args:     (e as any).args,
            index:    e.logIndex
        } satisfies TxHistoryLog;
    }

    private initBalancesUpdates(): void {
        this.txConfirmed$.pipe(
            tap(th => {
                const wAddresses: string[] = [th.from];
                const dIds: DeliverableId[] = [
                    this.dexSecuritiesSubjectService.$nativeDel()!.deliverableId
                ];

                console.log('[TxStatusMonitoring] initBalancesUpdates', {
                    th
                }); // TODO

                switch (th.metadata?.method) {
                    case TxHistoryRecMethod.DEPOSIT: {
                        const logsMeta = th.logsMeta as TxHistoryDepositLogsMeta;
                        const depositEvent = logsMeta?.depositEvents?.[0];
                        const orderEnteredEvent = logsMeta?.orderEnteredEvent;

                        if (orderEnteredEvent) {
                            dIds.push(orderEnteredEvent.deliverableId);
                        } else if (depositEvent) {
                            const sentRec = depositEvent.sent;
                            dIds.push(Number(sentRec.deliverableId));

                            const receivedRec = depositEvent.received;
                            const trId = Number(receivedRec.trancheId);
                            const tr = this.dexMdSubjectService.trancheMapById.get(trId);

                            if (tr) {
                                dIds.push(tr.unitDeliverableId);
                            }
                        }
                        break;
                    }
                    case TxHistoryRecMethod.WITHDRAW: {
                        const withdrawMeta = th.metadata as TxHistoryWithdrawMeta;
                        const logsMeta = th.logsMeta as TxHistoryWithdrawLogsMeta;
                        const orderEnteredEvent = logsMeta?.orderEnteredEvent;

                        if (orderEnteredEvent) {
                            dIds.push(orderEnteredEvent.deliverableId);
                        } else if (withdrawMeta) {
                            const trId = Number(withdrawMeta.trancheId);
                            const tr = this.dexMdSubjectService.trancheMapById.get(trId);

                            if (tr) {
                                dIds.push(tr.unitDeliverableId);
                            }
                        }

                        if (logsMeta) {
                            const withdrawEvent = logsMeta.withdrawalEvents?.[0];

                            if (withdrawEvent?.assetsWithdrawn.length) {
                                withdrawEvent.assetsWithdrawn.forEach(a =>
                                    dIds.push(Number(a.deliverableId))
                                );
                            }
                        }
                        break;
                    }
                    case TxHistoryRecMethod.TRANSFER: {
                        wAddresses.push(th.to);
                        const transferMeta = th.metadata as TxHistoryTransferMeta;

                        if (transferMeta) {
                            dIds.push(Number(transferMeta.deliverableId));
                        }

                        break;
                    }
                }

                if (dIds.length) {
                    this.erc20BalanceUpdatesService.notifyUpdates({wAddresses, dIds});
                }
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

}
